/**
 * Utility class for managing secure caching of API responses
 */
class CacheManager {
  constructor(storage = 'session') {
    this.storageType = storage === 'local' ? localStorage : sessionStorage;
    this.prefix = 'secure_cache_';
    this.defaultDuration = 10 * 60 * 1000; // 10 minutes
    this.version = '1.0'; // Cache version - increment when API response structure changes
    this.debug = process.env.NODE_ENV === 'development';
  }

  /**
   * More secure encryption using AES
   * @param {Object} data - Data to encrypt
   * @returns {string} - Encrypted data
   */
  async _encrypt(data) {
    try {
      // Convert the data to a string
      const text = JSON.stringify({
        version: this.version,
        data: data,
      });

      // Convert text to base64
      // TODO: In future use a proper encryption library like crypto-js
      const encrypted = btoa(unescape(encodeURIComponent(text)));

      // Add a simple checksum
      const checksum = await this._generateChecksum(text);
      return `${encrypted}.${checksum}`;
    } catch (error) {
      this._logError('Encryption failed:', error);
      return null;
    }
  }

  /**
   * Decrypt data with version checking
   * @param {string} encryptedData - Data to decrypt
   * @returns {Object|null} - Decrypted data or null if invalid
   */
  async _decrypt(encryptedData) {
    try {
      const [encrypted, storedChecksum] = encryptedData.split('.');

      // Decrypt the data
      const decrypted = decodeURIComponent(escape(atob(encrypted)));

      // Verify checksum
      const calculatedChecksum = await this._generateChecksum(decrypted);
      if (calculatedChecksum !== storedChecksum) {
        this._logError('Checksum verification failed');
        return null;
      }

      const { version, data } = JSON.parse(decrypted);

      // Version check
      if (version !== this.version) {
        this._log('Cache version mismatch, invalidating cache');
        return null;
      }

      return data;
    } catch (error) {
      this._logError('Decryption failed:', error);
      return null;
    }
  }

  /**
   * Generate a simple checksum for data integrity
   * In production, use a proper hashing algorithm
   */
  async _generateChecksum(text) {
    const encoder = new TextEncoder();
    const data = encoder.encode(text);
    const hashBuffer = await crypto.subtle.digest('SHA-256', data);
    const hashArray = Array.from(new Uint8Array(hashBuffer));
    return hashArray.map((b) => b.toString(16).padStart(2, '0')).join('');
  }

  /**
   * Get cached data if it exists and is not expired
   */
  async get(key) {
    try {
      const cacheKey = this.prefix + key;
      const item = this.storageType.getItem(cacheKey);

      if (!item) {
        this._log(`Cache miss for key: ${key}`);
        return null;
      }

      const decrypted = await this._decrypt(item);
      if (!decrypted) {
        this._log(`Cache invalid for key: ${key}`);
        this.remove(key);
        return null;
      }

      const { value, timestamp, expiry } = decrypted;

      if (Date.now() - timestamp > expiry) {
        this._log(`Cache expired for key: ${key}`);
        this.remove(key);
        return null;
      }

      this._log(`Cache hit for key: ${key}`);
      return value;
    } catch (error) {
      this._logError(`Cache get failed for key: ${key}`, error);
      this.remove(key);
      return null;
    }
  }

  /**
   * Set cache data with encryption
   */
  async set(key, value, duration = this.defaultDuration) {
    try {
      const item = {
        value,
        timestamp: Date.now(),
        expiry: duration,
      };

      const encrypted = await this._encrypt(item);
      if (encrypted) {
        this.storageType.setItem(this.prefix + key, encrypted);
        this._log(`Cache set for key: ${key}`);
      }
    } catch (error) {
      this._logError(`Cache set failed for key: ${key}`, error);
    }
  }

  /**
   * Remove cached item
   */
  remove(key) {
    try {
      this.storageType.removeItem(this.prefix + key);
      this._log(`Cache removed for key: ${key}`);
    } catch (error) {
      this._logError(`Cache remove failed for key: ${key}`, error);
    }
  }

  /**
   * Clear all cached items with our prefix
   */
  clear() {
    try {
      Object.keys(this.storageType).forEach((key) => {
        if (key.startsWith(this.prefix)) {
          this.storageType.removeItem(key);
        }
      });
      this._log('Cache cleared');
    } catch (error) {
      this._logError('Cache clear failed:', error);
    }
  }

  /**
   * Create a cacheable version of an API method
   */
  createCacheableMethod(apiMethod, cacheKey, duration = this.defaultDuration) {
    return async (...args) => {
      // Try to get from cache first
      const cached = await this.get(cacheKey);
      if (cached) {
        return cached;
      }

      // If not in cache or expired, call the API
      const result = await apiMethod(...args);

      // Only cache successful responses
      if (result && !result.error) {
        await this.set(cacheKey, result, duration);
      }

      return result;
    };
  }

  /**
   * Debug logging
   */
  _log(message) {
    if (this.debug) {
      console.log(`[CacheManager] ${message}`);
    }
  }

  _logError(message, error) {
    if (this.debug) {
      console.error(`[CacheManager] ${message}`, error);
    }
  }
}

export const cacheManager = new CacheManager('session');
